import React, { FC } from 'react'

import { calculatePriceWithVAT } from '@smarty-nx/code-utils'
import { Typography } from '@smarty-nx/ds'
import { Link } from 'gatsby'

import { content } from './country.content'
import PageHead from '../../components/PageHead'
import {
  BreadCrumbs,
  Card,
  Container,
  Desktop,
  Mobile,
  RoamingHeading,
  RoamingParagraph,
  SiteContainer,
  Table,
  TdPricing,
  TopFlag,
} from '../../components/shared'
import classes from '../../components/shared/roaming.module.scss'
import withApp from '../../components/withApp/withApp'
import { CONFIG } from '../../config'
import { PAGE_CONSTANTS } from '../../constants/globalStrings'

interface CountryPageContext {
  display_country: string
  international_calls: number
  international_sms: number
  international_mms: number
  international_video_mms: number
  iso_country_code: string
}

interface CountryProps {
  pageContext: CountryPageContext
  location: { pathname: string }
}

const Country: FC<CountryProps> = ({
  pageContext: {
    display_country,
    international_calls,
    international_sms,
    international_mms,
    international_video_mms,
    iso_country_code,
  },
}) => {
  return (
    <SiteContainer>
      <BreadCrumbs>
        <Desktop>
          <Link to={PAGE_CONSTANTS.CALLING_ABROAD.route}>
            {content.breadcrumbRoot}
          </Link>
          <Typography
            component="span"
            variant="Kollektif/Small"
            className={classes.navLink}
          >
            {'>'}
          </Typography>
          <Link to={PAGE_CONSTANTS.SELECT_ANOTHER_COUNTRY.route}>
            {content.breadcrumbCurrent}
          </Link>
          <Typography
            component="span"
            variant="Kollektif/Small"
            className={classes.navLink}
          >
            {'>'}
          </Typography>
          {display_country}
        </Desktop>
        <Mobile>
          {'< '}
          <Link
            to={PAGE_CONSTANTS.SELECT_ANOTHER_COUNTRY.route}
            data-testid={PAGE_CONSTANTS.SELECT_ANOTHER_COUNTRY.testId}
          >
            {content.breadcrumbMobile}
          </Link>
        </Mobile>
      </BreadCrumbs>
      <TopFlag
        src={`/assets/images/flags/${iso_country_code.toLowerCase()}.png`}
        alt={iso_country_code}
        data-testid={`${iso_country_code}-flag`}
      />
      <RoamingHeading data-testid="country-prices-title">
        {content.heading(display_country)}
      </RoamingHeading>
      <Container bgColor="#82c6ff" padding="15px">
        <Table borderColor="#79B7EB" data-testid="country-prices-table">
          <thead>
            <tr>
              <th />
              <th />
            </tr>
          </thead>
          <tbody>
            <tr>
              <td data-testid="calls-to-mobiles-text">
                {content.callsToMobiles}
              </td>
              <TdPricing data-testid="calls-to-mobiles-price">
                {calculatePriceWithVAT(international_calls)}
                <span>{content.unitMin}</span>
              </TdPricing>
            </tr>

            <tr>
              <td data-testid="calls-to-landlines-text">
                {content.callsToLandlines}
              </td>
              <TdPricing data-testid="calls-to-landlines-price">
                {calculatePriceWithVAT(international_calls)}
                <span>{content.unitMin}</span>
              </TdPricing>
            </tr>

            <tr>
              <td data-testid="calls-to-other-text">{content.callsToOther}</td>
              <TdPricing data-testid="calls-to-other-price">
                {calculatePriceWithVAT(international_calls)}
                <span>{content.unitMin}</span>
              </TdPricing>
            </tr>

            <tr>
              <td data-testid="sms-text">{content.texts}</td>
              <TdPricing data-testid="sms-price">
                {calculatePriceWithVAT(international_sms)}
                <span>{content.unitSms}</span>
              </TdPricing>
            </tr>

            <tr>
              <td data-testid="picture-message-text">{content.pictureMms}</td>
              <TdPricing data-testid="picture-message-price">
                {calculatePriceWithVAT(international_mms)}
                <span>{content.unitMms}</span>
              </TdPricing>
            </tr>

            <tr>
              <td data-testid="video-message-text">{content.videoMms}</td>
              <TdPricing data-testid="video-message-price">
                {calculatePriceWithVAT(international_video_mms)}
                <span>{content.unitMms}</span>
              </TdPricing>
            </tr>

            <tr>
              <td>{content.other}</td>
              <TdPricing>
                <span>
                  <a
                    href={content.priceGuideLink}
                    rel="noopener noreferrer"
                    target="_blank"
                    aria-label={content.seeAllPricesAriaLabel}
                  >
                    {content.seeAllPricesText}
                  </a>
                </span>
              </TdPricing>
            </tr>
          </tbody>
        </Table>
        <Card headingColor="#82c6ff">
          <h2 className={classes.cardHeadingBlue}>{content.cardHeading}</h2>
          <h2>{content.cardSubheading}</h2>
          <p>{content.cardBody}</p>
          <a
            href={`${CONFIG.DASHBOARD_URL}cash-addons`}
            className={classes.setupLink}
          >
            {content.cardLinkText}
          </a>
        </Card>
      </Container>
      <RoamingParagraph>{content.vatNote}</RoamingParagraph>
    </SiteContainer>
  )
}

export default withApp(Country)

export const Head: FC<CountryProps> = ({ pageContext, location }) => (
  <PageHead
    title={content.pageTitle(pageContext.display_country)}
    location={location}
  />
)
